/**
 * Sprint 24 — `/connections/m/:id` MCP connection detail.
 *
 * Mirrors the Wrapped detail page composition: hero strip, server/auth cards,
 * discovered tools, equipped-on-hires, recent calls, endpoint URL, danger zone.
 * Reads useConnection(id); Test+refresh re-discovers tools; Remove deletes.
 */

import { useState, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router";
import { Breadcrumb, showToast } from "../components/primitives";
import { ActionButtonRow } from "../components/shell/ActionButtonRow";
import { ConnectionTypeChip } from "../components/connections/ConnectionTypeChip";
import { ConnectionStatusPill } from "../components/connections/ConnectionStatusPill";
import { EquippedOnHires } from "../components/connections/EquippedOnHires";
import { EndpointUrlCard } from "../components/connections/EndpointUrlCard";
import { DangerZone } from "../components/primitives/DangerZone";
import {
  type Connection,
  type UnifiedConnectionStatus,
  useConnection,
  useTestConnection,
  useRemoveConnection,
} from "../../hooks/use-connections";
import { useFirstPartyTemplateMap } from "../../hooks/use-first-party-templates";
import { useAuth } from "../../lib/hooks/use-auth";
import { useChatScope } from "../hooks/use-chat-scope";

function pillStatus(c: Connection): UnifiedConnectionStatus {
  if (c.status === "connected") return "connected";
  if (c.status === "error") return "error";
  return c.auth_type === "oauth2" ? "oauth-needed" : "credentials-needed";
}

function authLabel(auth: Connection["auth_type"]): string {
  switch (auth) {
    case "none":
      return "No authentication";
    case "bearer":
      return "Bearer token";
    case "header":
      return "Custom header";
    case "oauth2":
      return "OAuth 2.0 (per-user)";
  }
}

export function McpConnectionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspaceId } = useAuth();
  const isOwner = user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role === "owner";

  const detail = useConnection(id);
  const testConnection = useTestConnection();
  const removeConnection = useRemoveConnection();
  const templateMap = useFirstPartyTemplateMap();
  const c = detail.data;

  useChatScope(
    { kind: "workspace", label: c?.name ?? "This connection" },
    c ? { kind: "mcp_connection", id: id ?? "", name: c.name } : null,
    [id, c?.name],
  );

  const [showAllTools, setShowAllTools] = useState(false);

  if (detail.isLoading) {
    return (
      <div style={page}>
        <div style={{ ...skeleton, height: 64 }} />
        <div style={grid2}>
          <div style={{ ...skeleton, height: 120 }} />
          <div style={{ ...skeleton, height: 120 }} />
        </div>
      </div>
    );
  }

  if (detail.error || !c) {
    return (
      <div style={page}>
        <div style={notFound}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Connection not found</div>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 4 }}>
            This connection doesn't exist or you don't have access.
          </div>
          <button type="button" style={backLink} onClick={() => navigate("/connections")}>
            ← Back to connections
          </button>
        </div>
      </div>
    );
  }

  const tools = c.tools ?? [];
  const headTools = showAllTools ? tools : tools.slice(0, 5);
  const transport = c.url === "https://graph.microsoft.com/v1.0" ? "Microsoft Graph" : "HTTP";

  async function testAndRefresh() {
    try {
      await testConnection.mutateAsync(id!);
      showToast("Connection tested — tools refreshed", "success");
    } catch {
      showToast("Test failed — check the connection", "error");
    }
  }

  async function remove() {
    await removeConnection.mutateAsync(id!);
    showToast("Connection removed", "success");
    navigate("/connections");
  }

  const subLine = [
    c.url,
    `${c.tool_count} tool${c.tool_count === 1 ? "" : "s"}`,
    c.last_tested ? `refreshed ${new Date(c.last_tested).toLocaleDateString()}` : "never tested",
  ].join(" · ");

  // Sprint 24.5 — first-party template brand override (backwards-compatible:
  // unresolved / non-first-party connections fall back to the default MCP brand).
  const template = c.first_party_template_slug
    ? templateMap.map[c.first_party_template_slug]
    : undefined;
  const avatarStyle: CSSProperties = template
    ? { ...avatar, background: template.brand.avatar_bg, color: template.brand.avatar_color }
    : avatar;
  const avatarIconName = template ? template.brand.icon_name : "plug";
  const chipType = template ? "mcp-first-party" : "mcp";

  return (
    <div style={page}>
      <Breadcrumb
        segments={[
          { label: "Connections", onClick: () => navigate("/connections") },
          { label: c.name },
        ]}
      />

      {/* Hero */}
      <div style={hero}>
        <span style={avatarStyle}>
          <i className={`ti ti-${avatarIconName}`} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={heroTitleRow}>
            <h1 style={heroName}>{c.name}</h1>
            <ConnectionTypeChip type={chipType} />
            <ConnectionStatusPill status={pillStatus(c)} />
          </div>
          <div style={heroSub} title={subLine}>
            {subLine}
          </div>
        </div>
        <ActionButtonRow
          actions={[
            {
              label: testConnection.isPending ? "Testing…" : "Test & refresh",
              icon: "refresh",
              onClick: testAndRefresh,
              disabled: testConnection.isPending,
            },
          ]}
        />
      </div>

      {/* Server + Auth */}
      <div style={grid2}>
        <section style={card}>
          <div style={cardHeading}>Server</div>
          <DetailRow label="URL" value={c.url} mono />
          <DetailRow label="Transport" value={transport} />
          <DetailRow label="Server name" value={c.server_name ?? "—"} />
          <DetailRow label="Version" value={c.server_version ?? "—"} />
        </section>
        <section style={card}>
          <div style={cardHeading}>Authentication</div>
          <DetailRow label="Method" value={authLabel(c.auth_type)} />
          {c.auth_type === "oauth2" && (
            <DetailRow label="Authorization" value="Each member authorises individually" />
          )}
          {c.error_message && <DetailRow label="Last error" value={c.error_message} danger />}
        </section>
      </div>

      {/* Discovered tools */}
      <section style={card}>
        <div style={toolsHeader}>
          <div style={cardHeading}>Discovered tools · {c.tool_count}</div>
          {tools.length > 5 && (
            <button type="button" style={seeAll} onClick={() => setShowAllTools((v) => !v)}>
              {showAllTools ? "Show less" : `See all ${tools.length} →`}
            </button>
          )}
        </div>
        {tools.length === 0 ? (
          <div style={muted}>No tools discovered yet. Run Test & refresh.</div>
        ) : (
          <div style={toolList}>
            {headTools.map((t) => (
              <div key={t.name} style={toolRow}>
                <span style={toolName}>{t.name}</span>
                {t.description && <span style={toolDesc}>{t.description}</span>}
              </div>
            ))}
            {!showAllTools && tools.length > 5 && (
              <div style={muted}>+ {tools.length - 5} more</div>
            )}
          </div>
        )}
      </section>

      {/* Equipped on + Recent calls */}
      <div style={grid2}>
        <EquippedOnHires connectionKind="mcp" connectionId={id!} />
        <section style={card}>
          <div style={cardHeading}>Recent calls</div>
          <div style={muted}>
            MCP tool calls run inside agent chats and aren't metered per-connection yet. Usage
            metrics arrive in a later release.
          </div>
        </section>
      </div>

      {/* Endpoint URL */}
      <EndpointUrlCard url={`/mcp/m/${id}`} />

      {/* Danger zone (owner only) */}
      {isOwner && (
        <DangerZone
          label="Remove connection"
          explainer="Agents lose access to this connection's tools. Equipment is cleared."
          confirmTitle={`Remove ${c.name}?`}
          confirmMessage="This connection and its tools will disappear from every agent equipped with it."
          confirmLabel="Remove connection"
          onConfirm={remove}
        />
      )}
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
  danger,
}: {
  label: string;
  value: string;
  mono?: boolean;
  danger?: boolean;
}) {
  return (
    <div style={detailRow}>
      <span style={detailLabel}>{label}</span>
      <span
        style={{
          ...detailValue,
          ...(mono ? { fontFamily: "var(--font-mono)", fontSize: 11 } : {}),
          ...(danger ? { color: "var(--color-text-danger)" } : {}),
        }}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

const page: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: "18px 20px",
  background: "var(--color-background-tertiary)",
  minWidth: 0,
  maxWidth: 920,
};

const hero: CSSProperties = { display: "flex", alignItems: "center", gap: 14 };

const avatar: CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: "var(--border-radius-md)",
  background: "#FAEEDA",
  color: "#854F0B",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 20,
  flexShrink: 0,
};

const heroTitleRow: CSSProperties = { display: "flex", alignItems: "center", gap: 8, minWidth: 0 };
const heroName: CSSProperties = { fontSize: 20, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const heroSub: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  marginTop: 3,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const grid2: CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };

const card: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  minWidth: 0,
  fontFamily: "var(--font-sans)",
};

const cardHeading: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
};

const detailRow: CSSProperties = { display: "flex", justifyContent: "space-between", gap: 12, minWidth: 0 };
const detailLabel: CSSProperties = { fontSize: 12, color: "var(--color-text-tertiary)", flexShrink: 0 };
const detailValue: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-primary)",
  textAlign: "right",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const toolsHeader: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between" };
const seeAll: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const toolList: CSSProperties = { display: "flex", flexDirection: "column", gap: 4, marginTop: 2 };
const toolRow: CSSProperties = { display: "flex", flexDirection: "column", gap: 1 };
const toolName: CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--color-text-primary)" };
const toolDesc: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const muted: CSSProperties = { fontSize: 12, color: "var(--color-text-tertiary)", lineHeight: 1.5 };

const skeleton: CSSProperties = {
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-secondary)",
  opacity: 0.6,
};

const notFound: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  padding: 32,
  textAlign: "center",
};

const backLink: CSSProperties = {
  marginTop: 12,
  fontSize: 13,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
